/**
 * Portfolio expirations (docs/11 F-3, partial; Matt 2026-08-25).
 *
 * Matt asked for the thing the Dashboard's "Needs attention" card had been
 * promising: one place that answers "what is coming due across my properties",
 * because a property manager with 40 buildings should not have to open 40 pages
 * to find out. Per-property expiry chips shipped with 0041/0042; this is the
 * portfolio-level read those chips imply.
 *
 * WHAT THIS IS NOT. docs/11 F-3 also specifies computed insights with
 * per-user snooze/acknowledge and task takeover by `source_key`. None of that
 * is here — there is no `insights` table and no snooze. This is a QUERY over
 * dates that already exist, which is why it needs no migration. The remaining
 * F-3 work is still open, and the Insights lane still says so rather than
 * pretending.
 *
 * ONE ENGINE, SERVER-SIDE. docs/11 F-3 records the warning from v1 explicitly:
 * "v1's two engines (a server bundle and a client hook with different windows)
 * is the warning, not the pattern." So the windows below are the single
 * authority, the server does the filtering, and the client renders what it is
 * given.
 */

import { z } from 'zod';

import { KEY_DATE_WINDOW_DAYS } from './key-dates.ts';

/**
 * The kinds of date this view watches.
 *
 * A UTILITY CONTRACT IS DELIBERATELY NOT A KIND. Since 0042 a utility
 * account's contract IS an `agreements` row — that was the whole point of
 * linking rather than duplicating the term — so it already arrives as
 * `agreement`. A separate kind would list one contract twice, and a list that
 * double-counts is a list people stop believing.
 *
 * BROKEN DOCUMENT LINKS are also absent, though the old placeholder copy
 * mentioned them: a broken link has no date, so it cannot be sorted or
 * windowed alongside things that do. It belongs on a verification screen, not
 * in a list ordered by when something lapses.
 */
export const EXPIRATION_KINDS = [
  'lease',
  'agreement',
  'management_agreement',
  'coi',
  'document',
  // A key date is not an expiration — it is something a person has to DO — but
  // for these lanes they behave identically: a due date, and a line either side
  // of today. One engine rather than two screens (Matt 2026-08-25).
  'key_date',
] as const;
export type ExpirationKind = (typeof EXPIRATION_KINDS)[number];

/**
 * How far ahead each kind is worth warning about. **THE authority** — the
 * server that filters and the client that labels both read this table and
 * nothing else, so a certificate and an agreement cannot disagree about what
 * "soon" means.
 *
 * Matt answered O-27 per-kind on 2026-08-27: *"I think 30 days for a COI, 90
 * days for agreement, utility contract, 180 days for leases."* Those are the
 * numbers below, and they are literals here rather than derived from
 * general-purpose constants elsewhere. That direction is deliberate and it is
 * the second time this file has been rearranged over it: `EXPIRING_SOON_DAYS`
 * promised that per-kind windows would become "a parameter, not a second
 * constant", then `LEASE_EXPIRING_SOON_DAYS` became exactly that second
 * constant, and this table was then DERIVED from the pair — which just moved
 * the authority somewhere a reader had to go and find. Both constants are now
 * gone and this is the only place a window is written down.
 *
 * Why each number:
 *
 * - **coi: 30** — a certificate lapsing in a month is a phone call to a broker.
 * - **agreement: 90** — a service contract is a negotiation with a notice
 *   period, not a phone call. **A utility contract IS an `agreements` row**
 *   since 0042, so Matt's "agreement, utility contract" both landing on 90 is
 *   the model agreeing with him rather than a coincidence: there is one number
 *   because there is one record.
 * - **management_agreement: 90** — NOT in Matt's answer. Assumed equal to a
 *   vendor agreement because it is structurally the same thing: a contract with
 *   a term and a fee. Recorded as docs/14 Q-10 / docs/17 FU-11 rather than
 *   chosen silently.
 * - **document: 30** — also not in Matt's answer, and left at the value it
 *   already had. A document's `valid_until` is usually a permit or certificate,
 *   which reads like a COI rather than like a contract. Same follow-up.
 * - **lease: 180** — a COI expiring in 30 days is a phone call; a lease
 *   expiring in 30 days is a crisis that started six months ago. Commercial
 *   renewal-notice clauses commonly run 6-12 months, so half a year is where
 *   the question becomes actionable rather than academic.
 * - **key_date: `KEY_DATE_WINDOW_DAYS`** — the one entry still derived, because
 *   a key date is not an expiration. It is something a person has to DO, and
 *   its horizon belongs to that feature.
 */
export const EXPIRATION_WINDOW_DAYS: Record<ExpirationKind, number> = {
  lease: 180,
  agreement: 90,
  // 90 to match the vendor agreement — MATT'S DECISION, 2026-08-30 (punch FU-11).
  // It had been my assumption since wave 4: his per-kind answer of 2026-08-27
  // covered COI, agreement, utility and lease and did not reach this kind.
  management_agreement: 90,
  coi: 30,
  document: 30,
  key_date: KEY_DATE_WINDOW_DAYS,
};

/** Reader-facing kind names. The UI never prints a raw enum. */
export const EXPIRATION_KIND_LABEL: Record<ExpirationKind, string> = {
  lease: 'Lease',
  agreement: 'Vendor agreement',
  management_agreement: 'Management agreement',
  coi: 'Certificate of insurance',
  document: 'Document',
  key_date: 'Key date',
};

/** One thing coming due, and everything needed to act on it. */
export interface ExpirationRow {
  readonly kind: ExpirationKind;
  /** Stable across kinds so React keys and dedup never collide. */
  readonly key: string;
  /** The record itself — what the row links to. */
  readonly entityId: string;
  readonly title: string;
  /** The supporting line: spaces, service, vendor, coverage. Never a duplicate of the title. */
  readonly detail: string | null;
  readonly expiration: string;
  /** Negative when already lapsed, which is how the lanes split. */
  readonly daysRemaining: number;
  readonly standing: 'expired' | 'expiring';
  /**
   * The property, when there is exactly ONE. A company-wide agreement or a
   * vendor's COI belongs to no single building, and saying so beats naming an
   * arbitrary one.
   */
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  /** "Company-wide", "12 properties", "Via Charlotte Office Portfolio". */
  readonly scopeLabel: string | null;
  readonly vendorId: string | null;
  readonly vendorName: string | null;
  /** Set where the record carries the flag; an auto-renewing term is not a deadline. */
  readonly autoRenew: boolean | null;
  /** Where to go to do something about it. */
  readonly href: string;
  /**
   * THE OVERLAY (0057). A derived deadline is ownable: a `key_dates` row may
   * carry a `source_key` naming this record instead of a date of its own, and
   * these four report it.
   *
   * `overlayId` is null until somebody acts — claims it, notes it, reschedules
   * it. The claim and note endpoints need that id, and its absence is what the
   * client reads to offer "claim" rather than "leave".
   *
   * The DATE is never here, deliberately: it stays `expiration`, read from the
   * lease or the agreement, so the item and its source cannot disagree.
   */
  readonly overlayId: string | null;
  /** `open` when nobody has closed it — which is also the unacted default. */
  readonly itemStatus: 'open' | 'done' | 'cancelled';
  /** Every current owner, in the order they joined. Null when unclaimed. */
  readonly ownerNames: string | null;
  /** Active progress notes on the item, so a row can say there is a thread. */
  readonly noteCount: number;
}

export interface ExpirationsResponse {
  readonly rows: readonly ExpirationRow[];
  /**
   * Which scope the server actually used.
   *
   * `mine` falls back to `all` when the caller holds no property assignments,
   * so this can differ from what was asked. The UI has to say which it is
   * showing — a list headed "my properties" that is quietly the whole portfolio
   * is a lie about coverage, and the reverse (an empty screen for an
   * administrator who holds no assignments by design) reads as a broken page.
   */
  readonly scopeApplied: 'mine' | 'all';
  /** Per-kind totals for the WHOLE result, so a truncated list still counts honestly. */
  readonly counts: Record<ExpirationKind, number>;
  readonly expiredCount: number;
  readonly expiringCount: number;
  /** True when `rows` was cut to `limit` — the UI says so rather than implying completeness. */
  readonly truncated: boolean;
}

export const expirationsQuerySchema = z.object({
  /**
   * `mine` = the properties the caller is assigned to; `all` = the portfolio.
   *
   * Default `mine`, because the question is "what do I have to deal with". A
   * caller with no materialized property access falls back to `all`
   * server-side rather than getting a near-empty screen — that is a read-only
   * user between approval and their first assignment. (An administrator or
   * coordinator is NOT that case: holding `properties.create` materializes
   * access to every property, so `mine` already means the portfolio for them.)
   */
  scope: z.enum(['mine', 'all']).default('mine'),
  /** `expired` and `soon` are the two lanes; `all` is both, newest deadline first. */
  lane: z.enum(['all', 'expired', 'soon']).default('all'),
  kinds: z
    .string()
    .transform((raw) => raw.split(',').filter((part) => part !== ''))
    .pipe(z.array(z.enum(EXPIRATION_KINDS)))
    .optional(),
  /** Narrow to one building — the property page's own "what is due here" view. */
  propertyId: z.uuid().optional(),
  /**
   * `me` = only deadlines somebody actually OWNS and that somebody is me.
   *
   * Orthogonal to `scope`, and the two are easy to confuse, so: `scope` is
   * about the PROPERTY ("do I hold this building"), `assignee` is about the
   * ITEM ("is my name on this piece of work"). A lease expiring on a building
   * I manage that nobody has claimed matches `scope=mine` and does not match
   * `assignee=me`, which is exactly the difference between the /work lanes and
   * the notification drawer (punch 3.3, docs/03 §7).
   *
   * Ownership means an unreleased `key_date_assignees` row (0047) on the item —
   * for a user-authored key date that is the row itself, and for a derived
   * deadline it is the 0057 overlay that made it claimable. An unclaimed
   * deadline has no owner and therefore matches nobody, which is why the
   * predicate is an EXISTS rather than a comparison against a nullable column.
   */
  assignee: z.enum(['anyone', 'me']).default('anyone'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type ExpirationsQuery = z.infer<typeof expirationsQuerySchema>;

/**
 * The counts behind the nav badge and the My Work chip (punch item 1.6).
 *
 * Deliberately the SAME numbers the /work lanes show, from the same query — a
 * badge that disagrees with the screen it points at trains people to ignore the
 * badge. `scopeApplied` travels with them for the same reason it travels with
 * the list: "3 items" means something different for my properties than for the
 * whole portfolio, and the caller has to be able to say which.
 */
export interface BadgeCounts {
  readonly actionItems: {
    readonly overdue: number;
    readonly dueSoon: number;
    /** Sum of the two, so the badge does not add up numbers itself. */
    readonly total: number;
  };
  /**
   * The bell (punch item 3.3), carried in the same request the chip already
   * makes rather than in a second ambient endpoint.
   *
   * A DIFFERENT number from `actionItems`, and the difference is the point:
   * `actionItems` counts every deadline across the properties this person
   * holds, and this counts only what is addressed to them personally and has
   * happened since they last opened the drawer.
   *
   * docs/03 §7 specifies a dot, and the bell renders one. The number is here
   * because a dot says nothing to a screen reader — the accessible name needs
   * to be able to say how many.
   */
  readonly notifications: {
    readonly unread: number;
  };
  readonly scopeApplied: 'mine' | 'all';
}
